import React, { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
    UserSliderThumb,
    SliderWrapper,
    WrongAnswerThumb,
    CorrectAnswerThumb
} from './Components';

import countdownAnimation from '../Widget/CountdownAnimation.json';
import { RadialAnswerTypes } from '../../utils/enums';
import { roundHalf } from '../../utils/radialSlider/helpers';
import { detect } from '../../utils/detect-browser';

let lottie = null;
const lottiePromise = import('lottie-web').then(l => (lottie = l.default));

const DEGREE_IN_RADIANS = Math.PI / 180;

class SliderUnit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            value: props.value,
            angle: this.valueToAngle(props.value),
            correctAnswerAngle: null,
            animationComplete: false,
            thumbAnimating: true
        };

        this.touches = [];
        this.allowChange = false;
        this.isDrag = false;
        this._wrapper = createRef();
        this._handle = createRef();
        this._answerHandle = createRef();
        this._answerArc = createRef();
        this._backgroundFill = createRef();
    }

    static propTypes = {
        allowClick: PropTypes.bool,
        allowReanswer: PropTypes.bool,
        animationDuration: PropTypes.number.isRequired,
        arcSize: PropTypes.number.isRequired,
        bgColor: PropTypes.string,
        correctValue: PropTypes.number,
        customColor: PropTypes.string.isRequired,
        customLogo: PropTypes.string.isRequired,
        defaultLogo: PropTypes.string.isRequired,
        isAnswered: PropTypes.bool.isRequired,
        isPoll: PropTypes.bool.isRequired,
        isSubmitting: PropTypes.bool.isRequired,
        correctAnswerAngle: PropTypes.number,
        max: PropTypes.number.isRequired,
        min: PropTypes.number.isRequired,
        onAfterChange: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        radius: PropTypes.number.isRequired,
        result: PropTypes.number,
        rotationOffset: PropTypes.number.isRequired,
        steps: PropTypes.number,
        stepSize: PropTypes.number,
        strokeWidth: PropTypes.number.isRequired,
        theme: PropTypes.object,
        thumbSize: PropTypes.number.isRequired,
        value: PropTypes.number.isRequired
    };

    static defaultProps = {
        arcSize: 360,
        bgColor: '#ccc',
        color: 'purple',
        max: 100,
        min: 0,
        radius: 100,
        rotationOffset: 0,
        sliced: false,
        steps: 0,
        stepSize: 0,
        strokeWidth: 35,
        thumbSize: 30,
        value: 0
    };

    lottieCallback() {
        if (
            lottie &&
            typeof lottie.loadAnimation === 'function' &&
            !!this._handle.current
        ) {
            this.lottieAnimation = lottie.loadAnimation({
                container: this._handle.current,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: countdownAnimation,
                progressiveLoad: false
            });
        }
    }

    destroyLottie() {
        if (lottie && typeof lottie.destroy === 'function' && !!this._handle.current) {
            this.lottieAnimation.destroy();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.correctValue !== null &&
            nextProps.max &&
            this.props.correctAnswerAngle !== nextProps.correctAnswerAngle
        ) {
            this.setState({
                correctAnswerAngle: nextProps.correctAnswerAngle
            });
            if (nextProps.result !== RadialAnswerTypes.correct) {
                const animationDelay = setTimeout(() => {
                    this.animateArc(
                        this.state.angle,
                        nextProps.correctAnswerAngle,
                        this._answerArc,
                        false,
                        nextProps.animationDuration
                    );
                    this.animateArc(
                        this.state.angle,
                        nextProps.correctAnswerAngle,
                        this._backgroundFill,
                        true,
                        nextProps.animationDuration
                    );
                    clearTimeout(animationDelay);
                }, 250);
            }
        }
        if (window.location.hash === '#isByobPreview' &&
            this.props.isAnswered && !nextProps.isAnswered){ //reset state for byob preview
            this.setState({
              "value": 0,
              "angle": 0,
              "correctAnswerAngle": null,
              "animationComplete": false,
              "thumbAnimating": true
            });
        }
        if (this.state.value !== nextProps.value) {
            this.setState({
                value: nextProps.value,
                angle: this.valueToAngle(nextProps.value),
                thumbAnimating: false
            });
        }
    }

    componentDidMount() {
        document.addEventListener('mouseup', this.up);
        if (!this.props.allowClick && this._wrapper.current) {
            this._wrapper.current.style.pointerEvents = 'none';
            this._wrapper.current.addEventListener('touchmove', e => e.preventDefault(), {
                passive: false
            });
        }
        lottiePromise.then(() => this.lottieCallback());
    }

    componentWillUnmount() {
        document.removeEventListener('mouseup', this.up);
        if (this._wrapper.current) {
            this._wrapper.current.removeEventListener(
                'touchmove',
                e => e.preventDefault(),
                {
                    passive: false
                }
            );
        }
    }

    up = e => {
        if (!this.isDrag) return;
        if (
            !this.props.allowClick &&
            this._wrapper.current &&
            (!this.props.isAnswered || this.props.allowReanswer)
        ) {
            this._wrapper.current.style.pointerEvents = 'none';
            this.props.onAfterChange && this.props.onAfterChange();
        }
        this.allowChange = false;
        this.isDrag = false;
        this.touches = [];
        e.stopPropagation();
        if (!this.state.thumbAnimating) this.destroyLottie();
    };

    getTouchMove = e => {
        e.stopPropagation();
        if (this.allowChange || this.isDrag) {
            let idx = 0;
            for (let index = 0; index < e.changedTouches.length; index++) {
                const t = e.changedTouches[index];
                if (t) {
                    this.touches = [t];
                    this.updateValue(this.touches[idx], false);
                }
            }
        }
    };

    down = e => {
        if (this.props.isSubmitting) return;
        if (
            this._wrapper.current &&
            (!this.props.isAnswered || this.props.allowReanswer)
        ) {
            this._wrapper.current.style.pointerEvents = 'auto';
        }
        e.stopPropagation();
        this.allowChange = true;
        this.isDrag = true;
        if (e.changedTouches) {
            this.touches.push(e.changedTouches);
        }
    };

    getArc = (startAngle, endAngle, closeArc = false) => {
        let { radius, strokeWidth } = this.props;
        const pathRadius = radius - strokeWidth / 2;
        const start = this.polarToCartesian({
            radius,
            pathRadius,
            angle: startAngle
        });
        const end = this.polarToCartesian({
            radius,
            pathRadius,
            angle: endAngle
        });

        const arcSweep = startAngle <= 180 ? 0 : 1;
        return `M ${start} A ${pathRadius} ${pathRadius} 0 ${arcSweep} 0 ${end} ${closeArc ? `L ${radius} ${radius} Z`: ''}`; //prettier-ignore
    };

    polarToCartesian({ pathRadius, angle, radius }) {
        const angleInRadians = (angle - 180) * DEGREE_IN_RADIANS;
        const x = radius + pathRadius * Math.cos(angleInRadians);
        const y = radius + pathRadius * Math.sin(angleInRadians);

        return x + ' ' + y;
    }

    getCenter() {
        if (this._wrapper.current) {
            const rect = this._wrapper.current.getBoundingClientRect();
            return {
                top: rect.top + this.props.radius,
                left: rect.left + this.props.radius
            };
        } else {
            return {
                top: 0,
                left: 0
            };
        }
    }

    limitValue = value => {
        const { min, max } = this.props;
        if (value < min) value = min;
        if (value > max) value = max;
        return value;
    };

    radToDeg(rad) {
        return rad * (180 / Math.PI);
    }

    angle(y, x) {
        const { rotationOffset } = this.props;
        let angle = this.radToDeg(Math.atan2(y, x)) + 180 - rotationOffset;
        if (angle > 360) {
            angle = angle - 360;
        }
        if (angle < 0) {
            angle = 360 + angle;
        }
        return angle;
    }

    angleToValue = angle => {
        const { min, max, arcSize } = this.props;
        const v = (angle / arcSize) * (max - min) + min;
        return v;
    };

    valueToAngle = value => {
        const { max, min, arcSize } = this.props;
        const angle = ((value - min) / (max - min)) * arcSize;
        return angle;
    };

    stepRounding(degree) {
        const { stepSize, steps, min, max, arcSize } = this.props;
        const step = stepSize || (steps ? (max - min) / steps : 1);
        const { angle: oldAngle } = this.state;
        let angToValue = min;
        if (!this.isDrag) {
            angToValue = this.angleToValue(degree);
        } else {
            angToValue = this.angleToValue(
                oldAngle > arcSize - 20 && degree < arcSize / 4
                    ? Math.max(degree, arcSize)
                    : oldAngle < 20 && degree > arcSize - 20
                    ? Math.min(degree, 0)
                    : degree
            );
        }
        let value;
        const remain = (angToValue - min) % step;
        const currVal = angToValue - remain;
        const nextVal = this.limitValue(currVal + step);
        const preVal = this.limitValue(currVal - step);
        if (angToValue >= currVal)
            value = angToValue - currVal < nextVal - angToValue ? currVal : nextVal;
        else {
            value = currVal - angToValue > angToValue - preVal ? currVal : preVal;
        }
        value = stepSize === 0.5 ? roundHalf(value).toFixed(1) : Math.round(value);
        const ang = this.valueToAngle(value);
        return { value, angle: ang };
    }

    updateValue = (event, forceSet) => {
        if (this.props.isAnswered && !this.props.allowReanswer) return;
        if (!this.isDrag && !forceSet) return;
        let eX = 0,
            eY = 0;
        const { clientX, clientY } = event;
        eX = clientX;
        eY = clientY;
        const { left, top } = this.getCenter();
        const x = eX - left,
            y = eY - top;
        const { value, angle } = this.stepRounding(this.angle(y, x));
        this.setState({ value, angle });
        this.props.onChange && this.props.onChange(value, this.props);
    };

    animateArc = (startAngle, endAngle, target, closeArc, animationDuration = 1200) => {
        let start;
        let position = startAngle;
        const distance = endAngle - startAngle;
        const step = timestamp => {
            if (!start) start = timestamp;
            let delta = (timestamp - start) / animationDuration;
            if (delta >= 1 && !!target.current) {
                target.current.setAttribute(
                    'd',
                    this.getArc(Math.min(endAngle, 359.9999), 0, closeArc)
                );
                this.setState({ animationComplete: true });
            } else if (!!target.current) {
                target.current.setAttribute(
                    'd',
                    this.getArc(Math.min(position, 359.9999), 0, closeArc)
                );
                position = distance * delta + startAngle;
                if (window) window.requestAnimationFrame(step);
            }
        };
        if (window) window.requestAnimationFrame(step);
    };

    render() {
        const {
            allowClick,
            allowReanswer,
            animationDuration,
            arcSize,
            bgColor,
            customColor,
            customLogo,
            defaultLogo,
            isAnswered,
            isPoll,
            isSubmitting,
            radius,
            result,
            rotationOffset,
            strokeWidth,
            theme,
            thumbSize
        } = this.props;
        const { angle, animationComplete, correctAnswerAngle } = this.state;
        const isCorrect = result === RadialAnswerTypes.correct;
        const size = radius * 2;
        const styleRotation = {
            transform: `rotate(${rotationOffset}deg)`,
            transformOrigin: '50% 50%'
        };
        const ieTransform = detect().name === 'ie' && 'translate(-38 97) rotate(-45)';
        return (
            <SliderWrapper
                ref={this._wrapper}
                onMouseMove={e =>
                    !isSubmitting &&
                    this.allowChange &&
                    (!isAnswered || allowReanswer) &&
                    this.updateValue(e, false)
                }
                onMouseDown={this.down}
                onTouchStart={this.down}
                onTouchMove={this.getTouchMove}
                onTouchEnd={this.up}
                onTouchCancel={this.up}
                allowClick={allowClick}
            >
                <Fragment>
                    <svg width={size} height={size}>
                        <defs>
                            <radialGradient id="fillGradient">
                                <stop offset="5%" stopColor="white" />
                                <stop offset="100%" stopColor="black" />
                            </radialGradient>
                        </defs>
                        <mask id="mask">
                            <circle
                                cx={'50%'}
                                cy={'50%'}
                                r={radius - strokeWidth}
                                fill="url('#fillGradient')"
                            />
                        </mask>
                        <path
                            mask="url(#mask)"
                            fill={customColor || theme.colors.primary}
                            ref={this._backgroundFill}
                            strokeWidth={strokeWidth}
                            style={{
                                ...styleRotation,
                                opacity: 0.15
                            }}
                            d={this.getArc(
                                Math.min(
                                    animationComplete ? correctAnswerAngle : angle,
                                    359.9999
                                ),
                                0,
                                true
                            )}
                            transform={ieTransform}
                        />
                        <path
                            fill="transparent"
                            strokeWidth={strokeWidth}
                            stroke={bgColor}
                            style={{
                                ...styleRotation,
                                opacity: '0.7'
                            }}
                            transform={ieTransform}
                            d={this.getArc(Math.min(arcSize, 359.9999), 0)}
                        />
                        <path
                            fill="transparent"
                            ref={this._answerArc}
                            strokeWidth={strokeWidth}
                            stroke={customColor || theme.colors.primary}
                            style={styleRotation}
                            d={this.getArc(
                                Math.min(
                                    animationComplete ? correctAnswerAngle : angle,
                                    359.9999
                                ),
                                0
                            )}
                            transform={ieTransform}
                        />
                    </svg>
                    <UserSliderThumb
                        ref={this._handle}
                        radius={radius}
                        visible={!isAnswered}
                        fadeOutOnAnswer={!isPoll}
                        rotateOffset={angle + rotationOffset}
                        thumbSize={thumbSize}
                        strokeWidth={strokeWidth}
                        customLogo={!!customLogo ? customLogo : defaultLogo}
                        customColor={customColor || theme.colors.primary}
                    />
                    <WrongAnswerThumb
                        color={theme.colors.secondary}
                        visible={isAnswered && !isCorrect &&
                            !(window.location.hash === '#isByobPreview')}
                        rotateOffset={angle + rotationOffset}
                        thumbSize={thumbSize}
                        strokeWidth={strokeWidth}
                    />
                    <CorrectAnswerThumb
                        visible={isAnswered && !isPoll}
                        animationDuration={animationDuration}
                        rotateOffset={angle + rotationOffset}
                        rotateOffsetFinal={
                            isCorrect
                                ? angle + rotationOffset
                                : correctAnswerAngle + rotationOffset
                        }
                        thumbSize={thumbSize}
                        strokeWidth={strokeWidth}
                        customColor={customColor || theme.colors.primary}
                    />
                </Fragment>
            </SliderWrapper>
        );
    }
}

export default SliderUnit;
